import { Message } from "@chatscope/chat-ui-kit-react";
import "../App.css";
import { getMessageDateFormatted } from "./getMessageDateFormatted";
import { TemplatePreview } from "../externalComponents/TemplatePreview";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ShoppingCartIcon,
} from "@heroicons/react/16/solid";
import "../externalComponents/TemplatePreview/template-preview.css";

export function generateMessageComponentFromContent(message) {
  let messageText = "";
  let imageLink = "";
  let videoLink = "";
  let voiceLink = "";
  let documentLink = "";
  let interactiveQuickReplyButtons = [];
  let interactiveUrlButton = null;
  let interactiveMessage = {};
  let template = {};
  let isTemplate = false;

  const direction = message.direction == "inbound" ? "incoming" : "outgoing";
  switch (message.content.type) {
    case "template": {
      isTemplate = true;
      const headerType = message.content.image
        ? "image"
        : message.content.video
          ? "video"
          : "none";
      template.body = message.content.body;
      template.headerType = headerType;

      if (headerType === "image") {
        template.imageUrl = message.content.image.link;
      } else if (headerType === "video") {
        template.videoUrl = message.content.video.link;
      }

      let quickReplyButtonsText = "";
      let urlButtonsText = "";

      const quickReplyButtons = message.content.quickReplyButtons;
      if (quickReplyButtons) {
        template.quickReply1 = quickReplyButtons[0]?.text || "";
        template.quickReply2 = quickReplyButtons[1]?.text || "";
        template.quickReply3 = quickReplyButtons[2]?.text || "";
      }
      const urlButtons = message.content.urlButtons;

      if (urlButtons) {
        template.urlButton = urlButtons[0]?.text || "";
        template.urlButton2 = urlButtons[1]?.text || "";
      }

      if (message.content.limitedTimeOffer) {
        template.limitedTimeOffer = message.content.limitedTimeOffer;
        template.copyCodeButton = message.content.copyCodeButton;
      }

      messageText = `${message.content.body} ${quickReplyButtonsText || urlButtonsText}`;
      if (message.content.image?.link) {
        imageLink = message.content.image.link;
      }
      if (message.content.video?.link) {
        videoLink = message.content?.video?.link;
      }
      if (message.content.cards) {
        template.cards = message.content?.cards;
      }
      break;
    }
    case "text":
      messageText = message.content.text.body;
      if (message.isQuickReply) {
        messageText = "↩️ " + messageText;
      }
      break;
    case "interactive": {
      interactiveMessage.body = message.content.body;
      interactiveQuickReplyButtons =
        message.content.interactive?.action?.buttons || [];

      interactiveUrlButton =
        message.content.interactive?.action?.urlButton || null;

      messageText = `${message.content.interactive?.body.text}`;
      const header = message.content.interactive.header;
      if (header?.image?.link) {
        imageLink = header.image.link;
      } else if (header?.video?.link) {
        videoLink = header.video.link;
      } else if (header?.document?.link) {
        documentLink = header.document.link;
      }
      break;
    }
    case "image":
      messageText = message.content.image.caption || message.content.image.body;
      imageLink = message.content.image.link;
      break;
    case "sticker":
      imageLink = message.content.sticker.link;
      break;
    case "video":
      messageText =
        message.content?.video?.caption || message.content?.video?.body;
      videoLink = message.content?.video?.link ?? message.content?.image?.link;
      break;
    case "document":
      messageText =
        message.content?.document?.caption || message.content?.document?.body;
      documentLink = message.content?.document?.link;
      break;
    case "inbound_image":
      imageLink = message.image.url;
      messageText = message.content?.caption ?? "";
      break;
    case "inbound_video":
      videoLink = message?.video?.url;
      messageText = message.content?.caption ?? "";
      break;
    case "inbound_voice":
      voiceLink = message?.voice?.url;
      break;
    case "inbound_document":
      documentLink =
        message?.content?.document?.link ||
        message?.content?.document?.url ||
        message?.document?.url;
      messageText = message.content?.caption ?? "";
      break;
    case "location":
      messageText =
        "[ADRESSE]\n" +
        message.content.location.name +
        "\n" +
        message.content.location.address;
      break;
    case "audio":
      voiceLink = message.content.audio?.link;
      break;
    case "order": {
      const order = message.content.order;
      const productItems = order?.product_items || [];

      // Calculate total quantity of items (sum of all quantities)
      const itemCount = productItems.reduce((total, item) => {
        return total + (item.quantity || 0);
      }, 0);

      // Calculate total from individual items
      let estimatedTotal = null;
      let currency = "€";
      if (productItems.length > 0) {
        estimatedTotal = productItems
          .reduce((total, item) => {
            return total + item.item_price * item.quantity;
          }, 0)
          .toFixed(2);
        currency = productItems[0].currency || "€";
      }

      messageText = "";
      if (itemCount > 0) {
        messageText += `${itemCount} ${itemCount === 1 ? "article" : "articles"}`;
      }
      if (estimatedTotal) {
        messageText += ` - ${estimatedTotal}${currency} (total estimé)`;
      }
      break;
    }
    default:
      messageText = "Erreur de chargement du message";
  }

  // Make it bold
  var boldSplit = messageText?.split("*");
  var textTokens = boldSplit?.map((token, index) => {
    return index % 2 == 0 ? token : <strong key={index}>{token}</strong>;
  });

  // Add shopping cart icon for order messages
  if (message.content.type === "order" && textTokens && textTokens.length > 0) {
    textTokens.unshift(
      <ShoppingCartIcon
        key="cart-icon"
        className="inline h-6 w-6 mr-2 text-gray-600"
      />,
    );
  }

  return (
    <Message
      key={message.id}
      model={{
        sentTime: "just now",
        direction: direction,
      }}
    >
      <Message.CustomContent>
        {!isTemplate && (
          <>
            <div
              className={
                window.innerWidth < 1040
                  ? "w-[16em]"
                  : "min-w-[24em] max-w-[30em]"
              }
            >
              {imageLink == "" ? null : (
                <img
                  alt={`Image d'en-tête`}
                  width="100%"
                  style={{ objectFit: "cover", objectPosition: "center" }}
                  src={imageLink}
                />
              )}
              {videoLink == "" ? null : (
                <video
                  controls
                  autoPlay={false}
                  muted={true}
                  alt={`Vidéo`}
                  width="100%"
                  style={{ objectFit: "cover", objectPosition: "center" }}
                  src={videoLink}
                />
              )}
              {voiceLink == "" ? null : (
                <audio
                  controls
                  autoPlay={false}
                  muted={false}
                  alt={`Vocal`}
                  src={voiceLink}
                />
              )}
              {documentLink == "" ? null : (
                <a target="_blank" href={documentLink} rel="noreferrer">
                  <iframe
                    alt={`Document`}
                    width="100%"
                    height="180px"
                    style={{
                      objectFit: "contain",
                      objectPosition: "center",
                      overflow: "hidden",
                      pointerEvents: "none",
                    }}
                    src={documentLink}
                    type="application/pdf"
                  />
                </a>
              )}

              <p
                className={`mt-2 ${window.innerWidth < 1040 ? "w-[16em]" : "min-w-[24em] max-w-[30em]"}`}
              >
                {textTokens}
              </p>

              {interactiveQuickReplyButtons.length > 0 && (
                <div className="buttons-preview">
                  {interactiveQuickReplyButtons.map((btn) => (
                    <div
                      key={btn.reply?.id || btn.id}
                      className="button-preview w-full flex items-center"
                    >
                      <ArrowUturnLeftIcon className="mr-1 h-[16px]" />{" "}
                      <p className="text-center w-full text-[#31795c]">
                        {btn.reply?.title || btn.title}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {interactiveUrlButton && (
                <div className="buttons-preview">
                  <div className="button-preview w-full flex items-center">
                    <ArrowTopRightOnSquareIcon className="mr-1 h-[16px]" />{" "}
                    <p className="text-center w-full text-[#31795c]">
                      {interactiveUrlButton.text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {isTemplate && (
          <div
            className={
              window.innerWidth < 1040
                ? "w-[16em]"
                : "min-w-[24em] max-w-[30em]"
            }
          >
            <TemplatePreview
              selectedHeader={template.headerType}
              imageUrl={template.imageUrl}
              videoUrl={template.videoUrl}
              body={template.body}
              cards={template.cards}
              quickReply1={template.quickReply1}
              quickReply2={template.quickReply2}
              quickReply3={template.quickReply3}
              urlButton={template.urlButton}
              urlButton2={template.urlButton2}
              isInConversation={true}
              width="100%"
              imageMaxHeight="100%"
              isLimitedTimeOffer={!!template.limitedTimeOffer}
              limitedTimeOffer={template.limitedTimeOffer}
              copyCodeButton={template.copyCodeButton}
            />
          </div>
        )}
      </Message.CustomContent>
      <Message.Footer>
        {getMessageDateFormatted(message.createdAt)}
      </Message.Footer>
    </Message>
  );
}
